import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';

export default function AuthLayout() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const location = useLocation();
  const isRegister = location.pathname === '/register';

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* ── Background Image ──────────────────── */}
      <div
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: `url('https://pump.amarfuel.com/assets/img/loginbg.jpg')`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundSize: 'cover',
        }}
      />

      {/* ── Dark overlay for readability ──────── */}
      <div className="absolute inset-0 z-[1] bg-gradient-to-b from-black/30 via-transparent to-black/50" />

      {/* ── Animated Bubbles ──────────────────── */}
      <BubbleAnimation />

      {/* ── Lottie Human Animation ────────────── */}
      <div className="absolute bottom-[15%] right-[5%] z-[3] hidden lg:block">
        {/* @ts-ignore */}
        <lottie-player
          src="https://assets10.lottiefiles.com/packages/lf20_khtt8ejx.json"
          background="transparent"
          speed="1"
          style={{ width: '280px', height: '280px' }}
          loop
          autoplay
        />
      </div>

      {/* ── Highway (scrolling road) ─────────── */}
      <div
        className="absolute bottom-0 left-0 right-0 z-[2] h-[80px]"
        style={{
          backgroundImage: `url('https://pump.amarfuel.com/assets/img/road.jpg')`,
          backgroundRepeat: 'repeat-x',
          backgroundSize: 'auto 100%',
          animation: 'highway 8s infinite linear',
        }}
      />

      {/* ── Car Animation ────────────────────── */}
      <div className="absolute bottom-[45px] left-[8%] z-[3] hidden md:block" style={{ animation: 'carBounce 1s infinite linear' }}>
        <img
          src="https://pump.amarfuel.com/assets/img/car.png"
          alt="Car"
          className="w-[280px] lg:w-[350px]"
        />
        {/* Front Wheel */}
        <img
          src="https://pump.amarfuel.com/assets/img/wheel.png"
          alt="Wheel"
          className="absolute bottom-[-8px] left-[8%] w-[18%]"
          style={{ animation: 'wheelSpin 3s infinite linear' }}
        />
        {/* Rear Wheel */}
        <img
          src="https://pump.amarfuel.com/assets/img/wheel.png"
          alt="Wheel"
          className="absolute bottom-[-8px] right-[14%] w-[18%]"
          style={{ animation: 'wheelSpin 3s infinite linear' }}
        />
      </div>

      {/* ── Form Card ────────────────────────── */}
      <div className="relative z-[10] flex min-h-screen items-center justify-center px-4 py-8 pb-28 md:pb-32">
        <div className={`w-full ${isRegister ? 'max-w-lg' : 'max-w-md'}`}>
          {/* Logo */}
          <div className="mb-5 text-center">
            <div className="inline-flex items-center gap-2.5 rounded-full bg-white/10 px-5 py-2.5 backdrop-blur-md">
              <span className="text-3xl">⛽</span>
              <div className="text-left">
                <h1 className="text-xl font-bold leading-tight text-white">Amar Fuel</h1>
                <p className="text-[10px] font-medium tracking-wider text-blue-200">
                  FUEL STATION ERP PLATFORM
                </p>
              </div>
            </div>
          </div>

          {/* Card */}
          <div className="rounded-2xl border border-white/20 bg-white/95 p-6 shadow-2xl backdrop-blur-xl sm:p-8">
            <Outlet />
          </div>

          {/* Footer Stats */}
          <div className="mt-4 flex justify-center gap-6">
            {[
              { value: '5K+', label: 'Stations' },
              { value: '10K+', label: 'Users' },
              { value: '99.9%', label: 'Uptime' },
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <p className="text-lg font-bold text-white drop-shadow-lg">{stat.value}</p>
                <p className="text-[10px] font-medium text-blue-200">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Keyframe Styles ──────────────────── */}
      <style>{`
        @keyframes highway {
          0% { transform: translateX(0); }
          100% { transform: translateX(-3400px); }
        }

        @keyframes carBounce {
          0%, 100% { transform: translateY(-1px); }
          50% { transform: translateY(1px); }
        }

        @keyframes wheelSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes bubbleFloat {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 0.6;
            border-radius: 0;
          }
          100% {
            transform: translateY(-1200px) rotate(720deg);
            opacity: 0;
            border-radius: 50%;
          }
        }
      `}</style>

      {/* ── Lottie Player Script ──────────────── */}
      <script src="https://unpkg.com/@lottiefiles/lottie-player@latest/dist/lottie-player.js" />
    </div>
  );
}

// ============================================
// Bubble Animation Component
// ============================================
function BubbleAnimation() {
  const bubbles = [
    { size: 80, left: '10%', delay: '0s', duration: '11s' },
    { size: 40, left: '20%', delay: '2s', duration: '12s' },
    { size: 50, left: '35%', delay: '4s', duration: '13s' },
    { size: 100, left: '50%', delay: '0s', duration: '14s' },
    { size: 30, left: '55%', delay: '1s', duration: '15s' },
    { size: 60, left: '65%', delay: '3s', duration: '12s' },
    { size: 120, left: '75%', delay: '7s', duration: '16s' },
    { size: 25, left: '80%', delay: '2s', duration: '11s' },
    { size: 50, left: '90%', delay: '4s', duration: '13s' },
    { size: 70, left: '45%', delay: '6s', duration: '14s' },
  ];

  return (
    <div className="absolute inset-0 z-[1] overflow-hidden">
      {bubbles.map((b, i) => (
        <div
          key={i}
          className="absolute bottom-[-160px]"
          style={{
            left: b.left,
            width: `${b.size}px`,
            height: `${b.size}px`,
            background: 'rgba(255,255,255,0.12)',
            animation: `bubbleFloat ${b.duration} infinite ease-in`,
            animationDelay: b.delay,
          }}
        />
      ))}
    </div>
  );
}
